/**
 * The entity-state rule set — rules that evaluate against the folded
 * current-value projection (`Record<entityId, EntityState>`) plus the
 * predicate registry. This is the v1 floor (§5.2); more rules (validity gaps)
 * and the pack-import severity-bump pass land incrementally. Causal cycles
 * (#222) established the directed-cycle pattern that containment cycles (#207)
 * reuse via the shared `analyzeDirectedGraph` detector.
 */
import type { RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

import { beliefContradictsCanon } from './belief-contradicts-canon.js';
import { cardinalityViolation } from './cardinality-violation.js';
import { causalCycle } from './causal-cycle.js';
import { containmentCycle } from './containment-cycle.js';
import { cursorSpoofRule } from './cursor-spoof.js';
import { cursorVisibilityRules } from './cursor-visibility.js';
import { facilitationModeMismatchRule } from './facilitation-mode-mismatch.js';
import { quickLogUnreviewed } from './quick-log-unreviewed.js';
import { recurrenceOpenCycle } from './recurrence-open-cycle.js';
import { refDangling } from './ref-dangling.js';
import { refWrongType } from './ref-wrong-type.js';
import { temporalGap } from './temporal-gap.js';
import { temporalLiveness } from './temporal-liveness.js';
import { valueKindMismatch } from './value-kind-mismatch.js';

/** A rule that reads the folded entity state and emits raw advisories. */
export type EntityStateRule = (ctx: AuditContext) => RawAdvisory[];

/** The v1 entity-state rules, in registration order. */
export const ENTITY_STATE_RULES: readonly EntityStateRule[] = [
  refDangling,
  refWrongType,
  valueKindMismatch,
  cardinalityViolation,
  temporalGap,
  temporalLiveness,
  beliefContradictsCanon,
  causalCycle,
  containmentCycle,
  recurrenceOpenCycle,
  cursorVisibilityRules,
  facilitationModeMismatchRule,
  cursorSpoofRule,
  quickLogUnreviewed,
];

export {
  beliefContradictsCanon,
  cardinalityViolation,
  causalCycle,
  containmentCycle,
  cursorSpoofRule,
  cursorVisibilityRules,
  facilitationModeMismatchRule,
  quickLogUnreviewed,
  recurrenceOpenCycle,
  refDangling,
  refWrongType,
  temporalGap,
  temporalLiveness,
  valueKindMismatch,
};
export { CAUSAL_CYCLE_RULE } from './causal-cycle.js';
export { CONTAINMENT_CYCLE_RULE, CYCLE_EXEMPT_CONTEXTS } from './containment-cycle.js';
export { CURSOR_SPOOF_ACTIVE_RULE } from './cursor-spoof.js';
export {
  CURSOR_VISIBILITY_LONG_BROWSE_RULE,
  CURSOR_VISIBILITY_MAP_HIDDEN_RULE,
} from './cursor-visibility.js';
export { FACILITATION_MODE_MISMATCH_RULE } from './facilitation-mode-mismatch.js';
export { QUICK_LOG_UNREVIEWED_RULE } from './quick-log-unreviewed.js';
export { RECURRENCE_OPEN_CYCLE_RULE } from './recurrence-open-cycle.js';
export { TEMPORAL_GAP_RULE } from './temporal-gap.js';
export { TEMPORAL_LIVENESS_RULE } from './temporal-liveness.js';
